<!--
 * @Author: Antigravity
 * @Date: 2026-03-23
 * @Description: 游客分析-来源地
-->

<template>
  <div class="w-full flex flex-col">
    <div class="w-full flex items-end justify-between my-16">
      <div class="text-16 font-bold text-black-primary">来源地</div>
    </div>

    <div class="card p-12">
      <div class="flex justify-between items-center mb-8">
        <span class="text-12 text-[#999]">单位（%）</span>
        <span class="text-12 text-[#999]">向左滑动可查看全部</span>
      </div>

      <div v-if="loading" class="state-text">加载中...</div>
      <div v-else-if="!list.length" class="state-text">暂无数据</div>
      <div v-else ref="chartEl" class="chart"></div>

      <div class="mt-12 text-center text-12 text-[#999]">
        无手机号码的投诉不计入统计
      </div>
    </div>
  </div>
</template>

<script>
import echarts from "echarts";
import APIS from "@/service/modules/complaintApis/modules/visualization.js";
import visualFilterMixin from "../../mixins/visualFilterMixin";

export default {
  name: "OriginCountCell",
  mixins: [visualFilterMixin],
  data() {
    return {
      chart: null,
      loading: false,
      list: [],
      _onResize: null
    };
  },
  watch: {
    list: {
      deep: true,
      handler() {
        this.$nextTick(() => this.renderChart());
      }
    }
  },
  mounted() {
    this.ensureChart();
  },
  beforeDestroy() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this.chart) this.chart.dispose();
  },
  methods: {
    ensureChart() {
      const el = this.$refs.chartEl;
      if (!el) return;
      // v-if 切换后 DOM 会重建，旧 chart 实例的 DOM 不再匹配
      if (this.chart) {
        if (typeof this.chart.getDom === 'function' && this.chart.getDom() !== el) {
          this.chart.dispose();
          this.chart = null;
        } else {
          return;
        }
      }
      this.chart = echarts.init(el);
      this._onResize = () => {
        if (this.chart) this.chart.resize();
      };
      window.addEventListener('resize', this._onResize, { passive: true });
    },
    buildOption() {
      const names = this.list.map(item => item.name || "-");
      const values = this.list.map(item => {
        const r = parseFloat(item.ratio);
        return isFinite(r) ? r : 0;
      });
      const total = names.length;
      const windowSize = 5;
      const startValue = 0;
      const endValue = total > 0 ? Math.min(windowSize - 1, total - 1) : 0;

      return {
        grid: { left: 40, right: 10, top: 20, bottom: 40, containLabel: false },
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          backgroundColor: 'rgba(54, 54, 54, 0.9)',
          padding: [10, 15],
          textStyle: { color: '#fff' },
          extraCssText: 'z-index: 999;', // 确保层级高于图形
          z: 999,
          formatter: (params) => {
            const dataIndex = params[0].dataIndex;
            const item = this.list[dataIndex];
            return `${item.name}<br/>投诉量: ${this.formatNumber(item.total)}<br/>占比: ${item.ratio}`;
          }
        },
        dataZoom: [
          {
            type: "inside",
            xAxisIndex: 0,
            zoomLock: true,
            startValue,
            endValue,
            moveOnMouseMove: true,
            moveOnMouseWheel: true,
            zoomOnMouseWheel: false
          }
        ],
        xAxis: {
          type: "category",
          data: names,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: "#E5E5E5" } },
          axisLabel: { color: "#333", fontSize: 12, interval: 0, margin: 12 }
        },
        yAxis: {
          type: "value",
          min: 0,
          max: 100,
          splitNumber: 5,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: "#999", fontSize: 12, formatter: '{value}%' },
          splitLine: { lineStyle: { color: "#F0F0F0", type: "solid" } }
        },
        series: [
          {
            type: "bar",
            data: values,
            barWidth: 15,
            barCategoryGap: "70%",
            itemStyle: {
              color: "#36B374",
              borderRadius: [6, 6, 0, 0]
            }
          }
        ]
      };
    },
    renderChart() {
      if (!this.list.length) return;
      this.$nextTick(() => {
        this.ensureChart();
        if (this.chart) {
          this.chart.setOption(this.buildOption(), true);
          this.chart.resize();
        }
      });
    },
    formatNumber(n) {
      if (!n && n !== 0) return "0";
      return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    onLoad() {
      const { areaId, beginTime, endTime, rule, channel } = this.filterParams;
      const params = {
        cityId: areaId,
        startDate: beginTime,
        endDate: endTime,
        rule: rule,
        channel: channel
      };

      this.loading = true;
      APIS.getOriginCount(params).then(res => {
        if (res && res.ret === 0) {
          this.list = Array.isArray(res.data) ? res.data : [];
        }
      }).finally(() => {
        this.loading = false;
        this.$nextTick(() => this.renderChart());
      });
    }
  }
};
</script>

<style lang="less" scoped>
.card {
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.04);
}

.chart {
  height: 280px;
  width: 100%;
}

.state-text {
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}
</style>
